import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Typography,
  Paper,
  Avatar,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close,
  Schedule as ScheduleIcon,
  AccessTime,
  Timer,
  Person,
  Add as AddIcon,
  Update as UpdateIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getWeeklyAssignmentByVisit } from "../../../api/services/scheduling";
import { QK } from "../../../base/qk";
import {
  formatTimeRange,
  getDurationFromTime,
  formatDateTime,
} from "../../../base/FormatTime";

const ScheduleModal = ({ show, handleClose, visitId }) => {
  const theme = useTheme();

  const { data: weeklyAssignment, isLoading } = useQuery(
    [QK.weeklyAssignment, visitId],
    () => getWeeklyAssignmentByVisit(visitId),
    { enabled: !!visitId }
  );

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ScheduleIcon
              sx={{ fontSize: 20, color: theme.palette.primary.main }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="#1A1A2E"
              fontSize="1rem"
            >
              Schedule Details
            </Typography>
            <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
              Weekly assignments for this visit
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          sx={{
            minWidth: 32,
            width: 32,
            height: 32,
            p: 0,
            color: "#6B7280",
            "&:hover": { bgcolor: "#F2F4F7" },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                {[
                  "Weekday",
                  "Time",
                  "Duration",
                  "Carer",
                  "Created",
                  "Updated",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      color: "#475467",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyAssignment?.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#F9FAFB" },
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={
                        item.weekly_day
                          ? item.weekly_day.substring(0, 3)
                          : "—"
                      }
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: 22,
                        bgcolor: "#F2F4F7",
                        color: "#475467",
                        border: "1px solid #EAECF0",
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <AccessTime sx={{ fontSize: 14, color: "#64748B" }} />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontSize="0.78rem"
                        color="#1A1A2E"
                        whiteSpace="nowrap"
                      >
                        {formatTimeRange(
                          item.time_required_from,
                          item.time_required_to
                        )}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Timer sx={{ fontSize: 14, color: "#F59E0B" }} />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontSize="0.78rem"
                        color="#1A1A2E"
                        whiteSpace="nowrap"
                      >
                        {getDurationFromTime(
                          item.time_required_from,
                          item.time_required_to
                        )}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {item.employee_details?.length > 0 ? (
                        item.employee_details.map((emp) => (
                          <Chip
                            key={emp.id}
                            avatar={
                              <Avatar
                                sx={{
                                  width: 18,
                                  height: 18,
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  bgcolor: theme.palette.primary.main,
                                  color: "#FFF",
                                }}
                              >
                                {emp.first_name?.charAt(0)}
                              </Avatar>
                            }
                            label={`${emp.first_name} ${
                              emp.last_name || ""
                            }`.trim()}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              height: 22,
                              bgcolor: alpha(
                                theme.palette.primary.main,
                                0.06
                              ),
                              color: theme.palette.primary.main,
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}`,
                              "& .MuiChip-avatar": { ml: 0.5 },
                            }}
                          />
                        ))
                      ) : (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.3,
                            borderRadius: "6px",
                            bgcolor: "#F8FAFC",
                            border: "1px dashed #E2E8F0",
                          }}
                        >
                          <Person sx={{ fontSize: 12, color: "#94A3B8" }} />
                          <Typography
                            variant="caption"
                            color="#94A3B8"
                            fontWeight={500}
                            fontSize="0.65rem"
                          >
                            Not Assigned
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Tooltip title={item.created_at || ""} arrow>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <AddIcon sx={{ fontSize: 12, color: "#039855" }} />
                        <Typography
                          variant="caption"
                          fontSize="0.72rem"
                          color="#475467"
                          fontWeight={500}
                        >
                          {formatDateTime(item.created_at)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Tooltip title={item.updated_at || ""} arrow>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <UpdateIcon sx={{ fontSize: 12, color: "#1570EF" }} />
                        <Typography
                          variant="caption"
                          fontSize="0.72rem"
                          color="#475467"
                          fontWeight={500}
                        >
                          {formatDateTime(item.updated_at)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!isLoading && weeklyAssignment?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      color="#94A3B8"
                      fontSize="0.8rem"
                    >
                      No weekly assignments found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#F9FAFB" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 2.5,
            "&:hover": {
              borderColor: "#98A2B3",
              bgcolor: "#F9FAFB",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleModal;